import * as XLSX from 'xlsx';

import { EntityType, PlatformPrivileges } from '../../../../types.generated';
import { EntityXlsxExpectedSheetHeader, GenericEntityProperties } from '../types';
import i18n from '../../../../i18n/i18n';

export function isDeleteDisabled(
    entityType: EntityType,
    entityData: GenericEntityProperties | null,
    platformPrivileges: PlatformPrivileges | null | undefined,
) {
    if (entityType === EntityType.GlossaryTerm || entityType === EntityType.GlossaryNode) {
        const entityHasChildren = !!entityData?.children?.total;
        const canManageGlossaryEntity = !!entityData?.privileges?.canManageEntity;
        const canDeleteGlossaryEntity = !entityHasChildren && canManageGlossaryEntity;
        return !canDeleteGlossaryEntity;
    }
    if (entityType === EntityType.DataProduct) {
        return false; // TODO: update with permissions
    }
    if (entityType === EntityType.Domain) {
        const entityHasChildren = !!entityData?.children?.total;
        const canManageDomains = !!platformPrivileges?.manageDomains;
        const canDeleteDomainEntity = !entityHasChildren && canManageDomains;
        return !canDeleteDomainEntity;
    }
    return false;
}

export function isMoveDisabled(
    entityType: EntityType,
    entityData: GenericEntityProperties | null,
    platformPrivileges: PlatformPrivileges | null | undefined,
) {
    if (entityType === EntityType.GlossaryTerm || entityType === EntityType.GlossaryNode) {
        const canManageGlossaryEntity = !!entityData?.privileges?.canManageEntity;
        return !canManageGlossaryEntity;
    }
    if (entityType === EntityType.Domain) {
        const canManageDomains = !!platformPrivileges?.manageDomains;
        return !canManageDomains;
    }
    return false;
}

export function shouldDisplayChildDeletionWarning(
    entityType: EntityType,
    entityData: GenericEntityProperties | null,
    platformPrivileges: PlatformPrivileges | null | undefined,
) {
    if (entityType === EntityType.GlossaryTerm || entityType === EntityType.GlossaryNode) {
        const entityHasChildren = !!entityData?.children?.total;
        const canManageGlossaryEntity = !!entityData?.privileges?.canManageEntity;
        const hasTooltip = entityHasChildren && canManageGlossaryEntity;
        return hasTooltip;
    }
    if (entityType === EntityType.Domain) {
        const entityHasChildren = !!entityData?.children?.total;
        const canManageDomains = !!platformPrivileges?.manageDomains;
        const hasTooltip = entityHasChildren && canManageDomains;
        return hasTooltip;
    }
    return false;
}

// Generate a sample file for download
export const generateSampleFile = (name, blob) => {
    // Create a download link
    const link = document.createElement('a');

    // Attach the downloadable link
    link.href = URL.createObjectURL(blob);
    link.download = name;

    // Append the link to the document and trigger the download
    document.body.appendChild(link);
    link.click();

    // Clean up by removing the link from the document
    document.body.removeChild(link);
};

const validateHeaders = (expectedHeaders: string[], actualHeaders: string[]): boolean => {
    return (
        expectedHeaders.length === actualHeaders.length &&
        actualHeaders.every((header) => expectedHeaders.includes(header))
    );
};

const findMissingSheets = (expected, actual) => {
    // Convert the actual sheets to a Set for faster lookup
    const actualSet = new Set(actual);

    // Filter the expected sheets to find those not present in the actual sheets
    const missing = expected.filter((sheet) => !actualSet.has(sheet));

    return missing;
};

export const validateHeadersXlsx = (
    workbook,
    expectedSheet: EntityXlsxExpectedSheetHeader[],
    validateMap: Record<string, boolean> = {},
): boolean => {
    const sheetNames = workbook.SheetNames;
    const sheetTrack: string[] = [];

    const validateResult = sheetNames.map((sheetName) => {
        if (sheetName === 'Reference Values') {
            return {
                isValid: true,
            };
        }
        // Skip validation for sheets that are marked as not to validate in validateMap
        if (validateMap[sheetName] === false) {
            return {
                isValid: true,
            };
        }

        // Find sheet that matches the name or is an extended name
        const matchedSheets = expectedSheet.filter((sample) => sheetName.startsWith(sample.sheetName));

        if (!matchedSheets.length) {
            return {
                header: i18n.t(`Invalid Sheet: {{sheetName}}`, { sheetName }),
                message: i18n.t(`Remove '{{sheetName}}' Sheet from the file.`, { sheetName }),
                isValid: false,
            };
        }

        // Find the closest match (you can adjust the logic here if needed)
        const matchedSheetName = matchedSheets.reduce((prev, curr) => {
            return curr.sheetName.length > prev.sheetName.length ? curr : prev;
        });

        // Check if we should validate this sheet based on validateMap
        if (validateMap[matchedSheetName.sheetName] === false) {
            sheetTrack.push(matchedSheetName.sheetName);
            return {
                isValid: true,
            };
        }

        // Get the actual sheet data
        const worksheet = workbook.Sheets[sheetName];
        const actualSheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1, range: 0 }) as string[][];

        if (!actualSheetData.length) {
            sheetTrack.push(matchedSheetName.sheetName);

            return {
                header: `Empty Sheet: ${sheetName}`,
                message: `Sheet '${sheetName}' is empty.`,
                isValid: false,
            };
        }

        sheetTrack.push(matchedSheetName.sheetName);

        // Validate headers (first row)
        const actualHeaders = actualSheetData[0];
        const expectedHeaders = matchedSheetName.sheetColumns;

        if (!validateHeaders(expectedHeaders, actualHeaders)) {
            return {
                header: `Header Mismatch: ${sheetName}`,
                message: `Headers in sheet '${sheetName}' do not match expected headers.\nExpected: ${expectedHeaders}, Found: ${actualHeaders}`,
                isValid: false,
            };
        }

        return {
            isValid: true,
        };
    });

    const expectedSheetHeaders: string[] = [];
    expectedSheet.forEach((sheet: EntityXlsxExpectedSheetHeader) => {
        if (sheet.validate) {
            expectedSheetHeaders.push(sheet.sheetName);
        }
    });

    const missingSheets = findMissingSheets(expectedSheetHeaders, sheetTrack);

    const missingSheetResult = missingSheets.map((sheet) => {
        // Skip validation for sheets marked as not to validate
        if (validateMap[sheet] === false) {
            return {
                isValid: true,
            };
        }

        return {
            header: i18n.t(`Not Found: {{sheet}}`, { sheet }),
            message: i18n.t(`Sheet '{{sheet}}' is missing from the file.`, { sheet }),
            isValid: false,
        };
    });

    return validateResult.concat(missingSheetResult);
};
